import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { CheckIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { createEnum } from "@/lib/utils/createEnum";
import { useTranslation } from "react-i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type M2Position = {
  id: number;
  date?: string;
  strategy_from_id?: number;
  strategy_from_name?: string;
  strategy_to_id?: number;
  strategy_to_name?: string;
  quantity?: number;
  weight?: number;
  market_value_local_ccy?: number;
  market_value_ptf_ccy?: number;
  time_serie_file_id?: number;
  unit_price?: number;
  au_pair?: number;
  percent_unit_price?: number;
  acquisition_cost?: number;
  premium_discount?: number;
  impairment_losses?: number;
  accrued_interests?: number;
  net_book_value?: number;
  tax_basis_value?: number;
  pledged_asset?: boolean;
  cic_code?: string;
};

export const m2PositionFields = createEnum<M2Position>();

export const useM2PositionColumns = () => {
  const { t } = useTranslation([Tables.M2_POSITIONS, "tables"]);

  const columns: ColumnDef<M2Position>[] = [
    {
      accessorKey: m2PositionFields.id,
      header: "Id",
    },
    {
      accessorKey: m2PositionFields.date,
      header: () => t("columns.date", { ns: Tables.M2_POSITIONS }),
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue(m2PositionFields.date)))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.strategy_from_name,
      header: () =>
        t("columns.strategy_from_name", { ns: Tables.M2_POSITIONS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_from_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_from_name}
        </Link>
      ),
    },
    {
      accessorKey: m2PositionFields.strategy_to_name,
      header: () => t("columns.strategy_to_name", { ns: Tables.M2_POSITIONS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_to_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_to_name}
        </Link>
      ),
    },
    {
      accessorKey: m2PositionFields.quantity,
      header: () => (
        <div className="text-right">
          {t("columns.quantity", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.quantity))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.weight,
      header: () => (
        <div className="text-right">
          {t("columns.weight", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.weight))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.market_value_local_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.market_value_local_ccy", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.market_value_local_ccy))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.market_value_ptf_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.market_value_ptf_ccy", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.market_value_ptf_ccy))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.unit_price,
      header: () => (
        <div className="text-right">
          {t("columns.unit_price", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.unit_price))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.au_pair,
      header: () => (
        <div className="text-right">
          {t("columns.au_pair", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.au_pair))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.percent_unit_price,
      header: () => (
        <div className="text-right">
          {t("columns.percent_unit_price", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.percent_unit_price))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.acquisition_cost,
      header: () => (
        <div className="text-right">
          {t("columns.acquisition_cost", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.acquisition_cost))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.premium_discount,
      header: () => (
        <div className="text-right">
          {t("columns.premium_discount", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.premium_discount))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.impairment_losses,
      header: () => (
        <div className="text-right">
          {t("columns.impairment_losses", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.impairment_losses))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.accrued_interests,
      header: () => (
        <div className="text-right">
          {t("columns.accrued_interests", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.accrued_interests))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.net_book_value,
      header: () => (
        <div className="text-right">
          {t("columns.net_book_value", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.net_book_value))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.tax_basis_value,
      header: () => (
        <div className="text-right">
          {t("columns.tax_basis_value", { ns: Tables.M2_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(m2PositionFields.tax_basis_value))}
        </div>
      ),
    },
    {
      accessorKey: m2PositionFields.pledged_asset,
      header: () => t("columns.pledged_asset", { ns: Tables.M2_POSITIONS }),
      cell: ({ row }) =>
        Boolean(row.getValue(m2PositionFields.pledged_asset)) && (
          <div className="flex justify-center">
            <CheckIcon />
          </div>
        ),
    },
    {
      accessorKey: m2PositionFields.cic_code,
      header: () => t("columns.cic_code", { ns: Tables.M2_POSITIONS }),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(m2PositionFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      ),
    },
  ];

  return columns;
};
